/* The outer viewport fits the active review; the swipe track keeps its natural height. */
if (!customElements.get('purra-review-carousel')) {
  customElements.define('purra-review-carousel', class extends HTMLElement {
    connectedCallback() {
      this.track=this.querySelector('[data-review-track]');
      this.viewport=this.querySelector('[data-review-viewport]');
      this.cards=Array.from(this.querySelectorAll('[data-review-card]'));
      if(!this.track||!this.viewport||!this.cards.length)return;
      this.track.style.removeProperty('height');
      this.events=new AbortController();
      const options={signal:this.events.signal};
      this.index=0;
      this.update=()=>{
        // Never retain a vertical offset when switching between reviews of different lengths.
        if(this.track.scrollTop!==0)this.track.scrollTop=0;
        const start=this.cards[0].offsetLeft;
        this.index=this.cards.reduce((nearest,card,i)=>Math.abs(card.offsetLeft-start-this.track.scrollLeft)<Math.abs(this.cards[nearest].offsetLeft-start-this.track.scrollLeft)?i:nearest,0);
        this.querySelectorAll('[data-review-index]').forEach(button=>button.setAttribute('aria-current',String(Number(button.dataset.reviewIndex)===this.index)));
        const prev=this.querySelector('[data-review-prev]'),next=this.querySelector('[data-review-next]');
        if(prev)prev.disabled=this.index===0;
        if(next)next.disabled=this.index===this.cards.length-1;
        const height=window.innerWidth<750&&this.cards[this.index].offsetHeight?this.cards[this.index].offsetHeight+'px':'';
        if(this.viewport.style.height!==height)this.viewport.style.height=height;
      };
      this.goTo=index=>{
        const target=Math.max(0,Math.min(this.cards.length-1,index));
        const reduced=window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollTo({left:this.cards[target].offsetLeft-this.cards[0].offsetLeft,behavior:reduced?'auto':'smooth'});
      };
      this.addEventListener('click',event=>{
        const button=event.target.closest('button');if(!button)return;
        if(button.hasAttribute('data-review-prev'))this.goTo(this.index-1);
        if(button.hasAttribute('data-review-next'))this.goTo(this.index+1);
        if(button.hasAttribute('data-review-index'))this.goTo(Number(button.dataset.reviewIndex));
      },options);
      this.track.addEventListener('scroll',this.update,{...options,passive:true});
      this.track.addEventListener('keydown',event=>{
        if(event.key==='ArrowRight'||event.key==='ArrowLeft'){
          event.preventDefault();this.goTo(this.index+(event.key==='ArrowRight'?1:-1));
        }
      },options);
      window.addEventListener('resize',this.update,options);
      this.addEventListener('load',this.update,{...options,capture:true});
      if(window.ResizeObserver){this.observer=new ResizeObserver(this.update);this.cards.forEach(card=>this.observer.observe(card));}
      const controls=this.querySelector('[data-review-controls]');if(controls)controls.hidden=false;
      this.update();
    }
    disconnectedCallback(){this.events?.abort();this.observer?.disconnect();if(this.viewport)this.viewport.style.height='';}
  });
}

/* Match the reference gallery's inline, silent looping playback on section load. */
if (!customElements.get('purra-moments-gallery')) {
  customElements.define('purra-moments-gallery', class extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('video').forEach(video => {
        video.setAttribute('playsinline', '');
        if (video.autoplay) video.play().catch(() => {});
      });
      const track=this.querySelector('.moments-cards');
      const cards=Array.from(this.querySelectorAll('.moments-card'));
      const controls=this.querySelector('[data-moments-controls]');
      if(!track||!cards.length||!controls)return;
      this.events=new AbortController();
      const options={signal:this.events.signal};
      let index=0;
      const position=i=>Math.max(0,Math.min(cards[i].offsetLeft-cards[0].offsetLeft,track.scrollWidth-track.clientWidth));
      const update=()=>{
        index=cards.reduce((nearest,card,i)=>Math.abs(position(i)-track.scrollLeft)<Math.abs(position(nearest)-track.scrollLeft)?i:nearest,0);
        controls.querySelectorAll('[data-moments-index]').forEach(button=>button.setAttribute('aria-current',String(Number(button.dataset.momentsIndex)===index)));
        controls.querySelector('[data-moments-prev]').disabled=index===0;
        controls.querySelector('[data-moments-next]').disabled=index===cards.length-1;
      };
      const goTo=i=>track.scrollTo({left:position(Math.max(0,Math.min(cards.length-1,i))),behavior:window.matchMedia?.('(prefers-reduced-motion: reduce)').matches?'auto':'smooth'});
      controls.addEventListener('click',event=>{
        const button=event.target.closest('button');if(!button)return;
        if(button.hasAttribute('data-moments-prev'))goTo(index-1);
        if(button.hasAttribute('data-moments-next'))goTo(index+1);
        if(button.hasAttribute('data-moments-index'))goTo(Number(button.dataset.momentsIndex));
      },options);
      track.addEventListener('scroll',update,{...options,passive:true});
      track.addEventListener('keydown',event=>{
        if(event.key==='ArrowLeft'||event.key==='ArrowRight'){
          event.preventDefault();goTo(index+(event.key==='ArrowRight'?1:-1));
        }
      },options);
      window.addEventListener('resize',update,options);
      if(window.ResizeObserver){this.observer=new ResizeObserver(update);this.observer.observe(track);}
      controls.hidden=false;
      update();
    }
    disconnectedCallback(){this.events?.abort();this.observer?.disconnect();}
  });
}

/* Daily countdown follows the visitor's local calendar, including DST changes. */
(() => {
  let scheduledTick;
  function updateCountdowns() {
    window.clearTimeout(scheduledTick);
    const timers=document.querySelectorAll('[data-midnight-countdown]');
    if(!timers.length)return;
    const now=new Date();
    const midnight=new Date(now.getTime());
    midnight.setHours(24,0,0,0);
    const remaining=Math.max(0,Math.ceil((midnight.getTime()-now.getTime())/1000));
    const hours=String(Math.floor(remaining/3600)).padStart(2,'0');
    const minutes=String(Math.floor(remaining%3600/60)).padStart(2,'0');
    const seconds=String(remaining%60).padStart(2,'0');
    timers.forEach(timer=>{
      timer.querySelector('[data-countdown-hours]').textContent=hours;
      timer.querySelector('[data-countdown-minutes]').textContent=minutes;
      timer.querySelector('[data-countdown-seconds]').textContent=seconds;
      timer.setAttribute('aria-label',hours+' hours, '+minutes+' minutes, '+seconds+' seconds until midnight in your local time');
      timer.hidden=false;
    });
    if(!document.hidden)scheduledTick=window.setTimeout(updateCountdowns,1000-now.getMilliseconds());
  }
  document.addEventListener('visibilitychange',updateCountdowns);
  document.addEventListener('shopify:section:load',updateCountdowns);
  window.addEventListener('pageshow',updateCountdowns);
  updateCountdowns();
})();

/* Reference offsets are calendar days from the visitor's local date, not business days. */
(() => {
  let scheduledUpdate;
  const months=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  function formatDate(date) {
    const day=date.getDate(),lastTwo=day%100;
    const suffix=lastTwo>=11&&lastTwo<=13?'th':({1:'st',2:'nd',3:'rd'}[day%10]||'th');
    return months[date.getMonth()]+' '+day+suffix;
  }
  function updateTimelines() {
    window.clearTimeout(scheduledUpdate);
    const timelines=document.querySelectorAll('[data-order-timeline]');
    if(!timelines.length)return;
    const now=new Date();
    const dateAfter=days=>{
      const date=new Date(now.getFullYear(),now.getMonth(),now.getDate(),12);
      date.setDate(date.getDate()+days);
      return formatDate(date);
    };
    timelines.forEach(timeline=>{
      timeline.querySelectorAll('[data-timeline-date]').forEach(el=>{
        const min=Number(el.dataset.minDays),max=Number(el.dataset.maxDays);
        el.textContent=dateAfter(min)+(max===min?'':' - '+dateAfter(max));
      });
      timeline.hidden=false;
    });
    const midnight=new Date(now.getTime());midnight.setHours(24,0,0,0);
    if(!document.hidden)scheduledUpdate=window.setTimeout(updateTimelines,midnight.getTime()-now.getTime()+50);
  }
  document.addEventListener('visibilitychange',updateTimelines);
  document.addEventListener('shopify:section:load',updateTimelines);
  document.addEventListener('shopify:section:unload',updateTimelines);
  window.addEventListener('pageshow',updateTimelines);
  updateTimelines();
})();

/* Progressive enhancement: Shopify forms remain usable without JavaScript. */
(() => {
  const root = () => window.Shopify?.routes?.root || '/';
  const drawer = () => document.getElementById('CartDrawer');
  const status = message => { const el=document.querySelector('[data-global-status]'); if(el) el.textContent=message; };
  const open = el => { if(el && !el.open) el.showModal(); };
  const panels = new WeakMap();
  const bundleWidgets = new WeakSet();
  let checkoutBusy = false;
  const bundleFor = panel => panel?.querySelector('kaching-bundles-block') ||
    (document.querySelectorAll('[data-product-panel]').length === 1 ? document.querySelector('kaching-bundles-block') : null);
  function bindBundle(widget) {
    if(!widget || bundleWidgets.has(widget))return;
    bundleWidgets.add(widget);
    widget.addEventListener('add-to-cart-requested',event=>{
      const panel=widget.closest('[data-product-panel]') || (document.querySelectorAll('[data-product-panel]').length===1?document.querySelector('[data-product-panel]'):null);
      const form=panel?.querySelector('.product-form');
      if(form)buyNow(form,event.detail?.items);
    });
  }
  function setMedia(panel,id) {
    if(!id || !panel.querySelector('[data-media-id="'+CSS.escape(id)+'"]')) return;
    panel.querySelectorAll('[data-media-id]').forEach(el=>{ el.hidden=el.dataset.mediaId!==id; if(el.hidden) el.querySelectorAll('video').forEach(v=>v.pause()); });
    panel.querySelectorAll('[data-media-target]').forEach(el=>el.setAttribute('aria-pressed',String(el.dataset.mediaTarget===id)));
  }
  function updateVariant(panel,changeUrl=false) {
    const select=panel.querySelector('[data-variant-select]');
    if(!select) return;
    const option=select.selectedOptions[0];
    if(!option) return;
    if(checkoutBusy)return;
    panel.querySelectorAll('[data-sticky-price]').forEach(el=>el.textContent=option.dataset.price);
    panel.querySelectorAll('[data-add-button]').forEach(el=>el.disabled=option.dataset.available!=='true');
    panel.querySelectorAll('[data-add-label]').forEach(el=>el.textContent=option.dataset.available==='true'?'Buy now':'Sold out');
    panel.querySelectorAll('[data-variant-id]').forEach(el=>el.setAttribute('aria-pressed',String(el.dataset.variantId===option.value)));
    setMedia(panel,option.dataset.media);
    if(changeUrl && document.body.classList.contains('template-product')){const url=new URL(location.href);url.searchParams.set('variant',option.value);history.replaceState({},'',url);}
  }
  function init(scope=document) {
    scope.querySelectorAll('kaching-bundles-block').forEach(bindBundle);
    scope.querySelectorAll('[data-product-panel]').forEach(panel=>{
      if(panels.has(panel)) return;
      const select=panel.querySelector('[data-variant-select]');
      const wrap=panel.querySelector('[data-variant-buttons]');
      if(select && wrap && select.options.length<=12){
        Array.from(select.options).forEach(option=>{
          const button=document.createElement('button');button.type='button';button.textContent=option.textContent;
          button.dataset.variantId=option.value;button.dataset.unavailable=String(option.dataset.available!=='true');
          button.setAttribute('aria-pressed',String(option.selected));wrap.append(button);
        });
        select.classList.add('sr-only');select.tabIndex=-1;
        wrap.setAttribute('role','group');wrap.setAttribute('aria-label','Choose product option');
      }
      updateVariant(panel);
      const form=panel.querySelector('.product-form'),sticky=panel.querySelector('[data-sticky-buy]');
      let observer;
      if(form && sticky){observer=new IntersectionObserver(entries=>{sticky.hidden=entries[0].boundingClientRect.bottom>=0;});observer.observe(form);}
      panels.set(panel,{observer});
    });
  }
  async function refreshCart(){
    const [markupResponse,cartResponse]=await Promise.all([fetch(root()+'?section_id=cart-drawer',{headers:{Accept:'text/html'}}),fetch(root()+'cart.js',{headers:{Accept:'application/json'}})]);
    if(!markupResponse.ok||!cartResponse.ok) throw new Error('Your bag could not be loaded. Please try again.');
    const [markup,cart]=await Promise.all([markupResponse.text(),cartResponse.json()]);
    const content=drawer()?.querySelector('[data-cart-content]');if(content) content.innerHTML=markup;
    document.querySelectorAll('[data-cart-count]').forEach(el=>el.textContent=cart.item_count);
    return cart;
  }
  async function changeCart(key,quantity){
    const response=await fetch(root()+'cart/change.js',{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({id:key,quantity})});
    const result=await response.json();
    if(!response.ok) throw new Error(result.description||result.message||'Unable to update this item.');
    await refreshCart();status('Shopping bag updated.');
  }
  let cartBusy=false;
  async function drawerUpdate(control,quantity){
    if(cartBusy) return;cartBusy=true;
    const currentDrawer=drawer();currentDrawer.setAttribute('aria-busy','true');
    currentDrawer.querySelectorAll('input,button').forEach(el=>el.disabled=true);
    try{await changeCart(control.dataset.key,quantity);currentDrawer.querySelector('[data-close-dialog]')?.focus();}
    catch(error){const el=currentDrawer.querySelector('[data-cart-error]');if(el)el.textContent=error.message;}
    finally{cartBusy=false;currentDrawer.removeAttribute('aria-busy');currentDrawer.querySelectorAll('input,button').forEach(el=>el.disabled=false);}
  }
  document.addEventListener('click',async event=>{
    const target=event.target.closest('button,a,dialog');if(!target)return;
    if(target.matches('[data-buy-selected]')){
      const panel=document.querySelector('[data-product-panel]');
      const button=panel?.querySelector('.product-form [data-add-button],.sample-buy');
      if(button){event.preventDefault();if(!checkoutBusy)button.click();}
    }
    if(target.matches('[data-open-dialog]'))open(document.getElementById(target.dataset.openDialog));
    if(target.matches('[data-close-dialog]'))target.closest('dialog')?.close();
    if(target.matches('dialog')){const r=target.getBoundingClientRect();if(event.clientX<r.left||event.clientX>r.right||event.clientY<r.top||event.clientY>r.bottom)target.close();}
    if(target.matches('[data-sample-size]')){target.parentElement.querySelectorAll('button').forEach(el=>el.setAttribute('aria-pressed',String(el===target)));}
    if(target.matches('[data-media-target]'))setMedia(target.closest('[data-product-panel]'),target.dataset.mediaTarget);
    if(target.matches('[data-variant-id]')){const panel=target.closest('[data-product-panel]');panel.querySelector('[data-variant-select]').value=target.dataset.variantId;updateVariant(panel,true);}
    if(target.matches('[data-open-cart]')){if(!drawer())return;event.preventDefault();try{await refreshCart();open(drawer());}catch{location.assign(target.href);}}
    if(target.matches('[data-remove-item]')&&target.closest('#CartDrawer')){event.preventDefault();await drawerUpdate(target,0);}
  });
  document.addEventListener('change',async event=>{
    const target=event.target;
    if(target.matches('[data-variant-select]'))updateVariant(target.closest('[data-product-panel]'),true);
    if(target.matches('[data-cart-quantity]')&&target.closest('#CartDrawer')){const qty=Number(target.value);if(!Number.isInteger(qty)||qty<0){target.reportValidity();return;}await drawerUpdate(target,qty);}
  });
  // Kaching's documented items() retains bundle properties, quantities and selling plans.
  async function buyNow(form,requestedItems) {
    if(checkoutBusy || form.dataset.submitting==='true')return;
    const panel=form.closest('[data-product-panel]');const errorEl=form.querySelector('[data-product-error]');
    if(!panel||!errorEl)return;
    errorEl.textContent='';form.dataset.submitting='true';checkoutBusy=true;
    document.querySelectorAll('[data-add-button]').forEach(el=>el.disabled=true);
    panel.querySelectorAll('[data-add-label]').forEach(el=>el.textContent='Taking you to checkout…');
    try{
      const widget=bundleFor(panel);
      let lines=requestedItems;
      if(widget){
        bindBundle(widget);
        if(typeof widget.validateItemSelection!=='function'||typeof widget.items!=='function')throw new Error('Your bundle is still loading. Please try again in a moment.');
        const validation=await widget.validateItemSelection();
        if(!validation.valid)throw new Error(validation.message||'Please complete your bundle selection above.');
        if(lines===undefined)lines=await widget.items();
      }else if(panel.querySelector('kaching-bundle')){
        throw new Error('Your bundle is still loading. Please try again in a moment.');
      }
      const options={method:'POST',headers:{Accept:'application/json'}};
      if(lines!==undefined){
        if(!Array.isArray(lines)||!lines.length||lines.some(line=>!line.id||!Number.isInteger(Number(line.quantity))||Number(line.quantity)<1))throw new Error('Please select your bundle before checking out.');
        options.headers['Content-Type']='application/json';options.body=JSON.stringify({items:lines});
      }else{
        options.body=new FormData(form);
      }
      const response=await fetch(root()+'cart/add.js',options);
      const result=await response.json();if(!response.ok)throw new Error(result.description||result.message||'This item could not be added. Please try again.');
      location.assign(root()+'checkout');
    }catch(error){
      errorEl.textContent=error.message;status(error.message);
      errorEl.scrollIntoView?.({block:'center',behavior:'auto'});
      form.dataset.submitting='false';checkoutBusy=false;
      document.querySelectorAll('[data-product-panel]').forEach(panel=>updateVariant(panel));
    }
  }
  document.addEventListener('submit',event=>{
    const form=event.target;
    if(!form.matches('.product-form')||event.defaultPrevented)return;
    event.preventDefault();buyNow(form);
  });
  document.addEventListener('kaching-bundles-block-loaded',event=>bindBundle(event.detail?.component));
  window.addEventListener('pageshow',()=>{
    checkoutBusy=false;
    document.querySelectorAll('.product-form').forEach(form=>form.dataset.submitting='false');
    document.querySelectorAll('[data-product-panel]').forEach(panel=>updateVariant(panel));
  });
  document.addEventListener('shopify:section:load',event=>init(event.target));
  document.addEventListener('shopify:section:unload',event=>{event.target.querySelectorAll('[data-product-panel]').forEach(panel=>panels.get(panel)?.observer?.disconnect());});
  document.addEventListener('keydown',event=>{if(event.key==='Escape')document.querySelectorAll('.mobile-menu[open]').forEach(el=>el.open=false);});
  init();
})();
